import { computed, unref } from "vue";
import { useHead } from "#imports";
import css from "@/assets/styles/free-tools.css?url";

/**
 * Drive <head> (meta, canonical, OG/Twitter, page CSS, fonts) + JSON-LD
 * structured data for a Free Tools SSR page.
 *
 * @param {import('vue').Ref|Object} seoRef — a ref (or plain object) holding
 *   the payload returned by getToolSEO(slug). Every field is guarded for null
 *   so the page never throws when the slug is unknown / partial.
 */
export function useFreeToolSeoHead(seoRef) {
  const seo = computed(() => unref(seoRef) || null);

  // ---- <title> ----
  const title = computed(() => seo.value?.title || "Free Tools | SharkFoto");

  // ---- <link> tags (canonical + page CSS + Google Fonts) ----
  const link = computed(() => {
    const out = [];
    if (seo.value?.canonical) {
      out.push({ rel: "canonical", href: seo.value.canonical });
    }
    out.push({ rel: "stylesheet", href: css });
    out.push({ rel: "preconnect", href: "https://fonts.googleapis.com" });
    out.push({
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossorigin: "",
    });
    out.push({
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Public+Sans:wght@400;500;600;700;800&display=swap",
    });
    return out;
  });

  // ---- <meta> tags (description + OG + Twitter) ----
  const meta = computed(() => {
    const s = seo.value;
    if (!s) return [];
    const out = [];
    if (s.desc) out.push({ name: "description", content: s.desc });

    // Open Graph (property=)
    if (s.title) out.push({ property: "og:title", content: s.title });
    if (s.desc) out.push({ property: "og:description", content: s.desc });
    if (s.ogImage) out.push({ property: "og:image", content: s.ogImage });
    if (s.url) out.push({ property: "og:url", content: s.url });
    out.push({ property: "og:type", content: "website" });
    out.push({ property: "og:site_name", content: "SharkFoto" });

    // Twitter (name=)
    out.push({ name: "twitter:card", content: "summary_large_image" });
    if (s.title) out.push({ name: "twitter:title", content: s.title });
    if (s.desc) out.push({ name: "twitter:description", content: s.desc });
    if (s.ogImage) out.push({ name: "twitter:image", content: s.ogImage });

    return out;
  });

  // ---- JSON-LD structured data ----
  const jsonLd = computed(() => {
    const s = seo.value;
    if (!s) return null;
    const graph = [];

    // SoftwareApplication (+ aggregateRating)
    const app = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: s.appName || s.name || "SharkFoto",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    };
    if (s.rating && s.rating.value != null && s.rating.count != null) {
      app.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: String(s.rating.value),
        ratingCount: String(s.rating.count),
      };
    }
    graph.push(app);

    // HowTo (from steps / stepsTitle)
    if (Array.isArray(s.steps) && s.steps.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: s.stepsTitle || s.name || "How it works",
        step: s.steps.map((st) => ({
          "@type": "HowToStep",
          name: st?.[0] || "",
          text: st?.[1] || "",
        })),
      });
    }

    // FAQPage (from faq)
    if (Array.isArray(s.faq) && s.faq.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: s.faq.map((f) => ({
          "@type": "Question",
          name: f?.[0] || "",
          acceptedAnswer: { "@type": "Answer", text: f?.[1] || "" },
        })),
      });
    }

    // BreadcrumbList (from crumb)
    if (Array.isArray(s.crumb) && s.crumb.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: s.crumb.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c?.[0] || "",
          item: c?.[1] || "",
        })),
      });
    }

    return graph;
  });

  const script = computed(() => {
    const graph = jsonLd.value;
    if (!graph || !graph.length) return [];
    return [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(graph),
      },
    ];
  });

  useHead({
    title,
    link,
    meta,
    script,
  });
}
